import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import DoshaChip from './DoshaChip';
import { getDoshas, isSama } from '../../types';

const DOSHA_INFO: Record<string, any> = {
    vata: {
        name: 'Vata',
        subtitle: 'The Energy of Movement',
        color: '#8B5CF6',
        gradient: 'from-purple-500 to-violet-600',
        icon: '💨',
        qualities: ['Dry', 'Light', 'Cold', 'Rough', 'Irregular'],
        governs: ['Movement', 'Circulation', 'Breathing', 'Elimination', 'Communication'],
        balanced: ['Creative', 'Energetic', 'Flexible', 'Quick-thinking', 'Joyful'],
        imbalanced: ['Anxious', 'Restless', 'Dry skin', 'Joint stiffness', 'Irregular digestion'],
        causes: [
            'Irregular meal times or sleep patterns',
            'Cold, dry, windy weather',
            'Mental stress and overwhelm',
            'Too much travel or change',
            'Excessive stimulation'
        ],
        goodNews: 'Vata responds quickly to warmth, routine, and grounding practices.',
    },
    pitta: {
        name: 'Pitta',
        subtitle: 'The Energy of Transformation',
        color: '#EF4444',
        gradient: 'from-red-500 to-orange-600',
        icon: '🔥',
        qualities: ['Hot', 'Sharp', 'Light', 'Oily', 'Intense'],
        governs: ['Digestion', 'Metabolism', 'Body temperature', 'Understanding', 'Courage'],
        balanced: ['Focused', 'Confident', 'Intelligent', 'Strong digestion', 'Warm'],
        imbalanced: ['Irritable', 'Inflamed', 'Heartburn', 'Skin rashes', 'Perfectionist'],
        causes: [
            'Hot, spicy, or acidic foods',
            'Hot weather or excessive heat exposure',
            'Overworking or perfectionism',
            'Competitive activities',
            'Skipping meals (increases internal fire)'
        ],
        goodNews: 'Pitta balances with cooling practices, moderation, and letting go.',
    },
    kapha: {
        name: 'Kapha',
        subtitle: 'The Energy of Structure',
        color: '#10B981',
        gradient: 'from-emerald-500 to-teal-600',
        icon: '🌊',
        qualities: ['Heavy', 'Slow', 'Cool', 'Oily', 'Stable'],
        governs: ['Structure', 'Lubrication', 'Fluid balance', 'Growth', 'Immunity'],
        balanced: ['Calm', 'Loving', 'Strong', 'Steady energy', 'Good endurance'],
        imbalanced: ['Sluggish', 'Heavy', 'Congested', 'Weight gain', 'Resistant to change'],
        causes: [
            'Heavy, oily, or sweet foods',
            'Lack of exercise or movement',
            'Cold, damp weather',
            'Excessive sleep or daytime naps',
            'Emotional holding or attachment'
        ],
        goodNews: 'Kapha balances with stimulation, variety, and invigorating practices.',
    }
};

interface PatternRevelationProps {
    detectedPattern: {
        vikritiDosha: string;
        prakritiDosha?: string;
        insights: string[];
    };
    userIntent?: 'wellness' | 'condition' | null;
    vikritiSkipped?: boolean;
    bodyConnections?: Array<{
        symptom: string;
        location: string;
        category: string;
    }>;
    lifestyleFactors?: Array<{
        icon: string;
        label: string;
    }>;
    emotionalState?: string[];
    onComplete: (destination?: string) => void;
    isSubmitting?: boolean;
}

export default function PatternRevelation({
    detectedPattern,
    userIntent = 'wellness',
    vikritiSkipped = false,
    bodyConnections = [],
    lifestyleFactors = [],
    emotionalState = [],
    onComplete,
    isSubmitting = false
}: PatternRevelationProps) {
    const navigate = useNavigate();

    // Determine which cards to show
    // Support both camelCase, snake_case, and 'primaryDosha' variant
    const vDosha = (detectedPattern as any)?.vikritiDosha || (detectedPattern as any)?.primaryDosha || (detectedPattern as any)?.vikriti_dosha;
    const pDosha = (detectedPattern as any)?.prakritiDosha || (detectedPattern as any)?.prakriti_dosha;
    
    const showVikriti = !vikritiSkipped && !!vDosha;
    const showPrakriti = !!pDosha;
    
    // Main dosha to display (the one that needs attention)
    const activeDosha = vDosha || pDosha || '';
    
    // We'll use vikritiDosha for the current state display
    const prakritiDosha = pDosha || '';
    const vikritiDosha = vDosha || '';

    const getCTAText = () => {
        if (userIntent === 'condition' && showVikriti) return 'Chat with Nani';
        if (userIntent === 'wellness' && vikritiSkipped) return 'Chat with Nani';
        if (showVikriti && showPrakriti) return 'Chat with Nani';
        return 'Chat with Nani'; // Simplified per request
    };

    const prakritiInfo = prakritiDosha ? DOSHA_INFO[prakritiDosha.toLowerCase().split('-')[0]] : null;
    const vikritiInfo = vikritiDosha ? DOSHA_INFO[vikritiDosha.toLowerCase().split('-')[0]] : null;

    return (
        <div className="w-full max-w-4xl mx-auto p-4 md:p-6 space-y-8 font-sans">
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center pt-8"
            >
                <h1 className="text-4xl font-serif font-bold text-sage-900 mb-4">
                    Your Health Pattern Revealed 🔍
                </h1>
                <p className="text-lg text-sage-700 bg-sage-50 inline-block px-6 py-2 rounded-full border border-sage-100">
                    Nani has decoded the elemental currents within you
                </p>
            </motion.div>

            {/* Result Cards Container */}
            <div className="space-y-6">
                {/* 1. Prakriti Card (Lifelong Nature) */}
                {showPrakriti ? (
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-white rounded-[2rem] shadow-xl shadow-sage-900/5 p-8 border-2 relative overflow-hidden group border-emerald-100"
                    >
                        <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-700">
                            <span className="text-9xl">
                                {isSama(prakritiDosha) ? '🌿' : getDoshas(prakritiDosha).map(d => DOSHA_INFO[d as keyof typeof DOSHA_INFO]?.icon).join('')}
                            </span>
                        </div>
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-6 relative z-10">
                            <div className="flex gap-1">
                                {isSama(prakritiDosha) ? (
                                    ['vata', 'pitta', 'kapha'].map(d => (
                                        <div key={d} className="text-4xl w-20 h-20 rounded-full flex items-center justify-center bg-emerald-50 shadow-inner border border-emerald-100">
                                            {DOSHA_INFO[d as keyof typeof DOSHA_INFO].icon}
                                        </div>
                                    ))
                                ) : (
                                    getDoshas(prakritiDosha).map(d => (
                                        <div key={d} className="text-4xl w-20 h-20 rounded-full flex items-center justify-center bg-emerald-50 shadow-inner border border-emerald-100">
                                            {DOSHA_INFO[d as keyof typeof DOSHA_INFO]?.icon || '🌿'}
                                        </div>
                                    ))
                                )}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h2 className="text-2xl font-serif font-bold text-emerald-800">
                                        Your Core Constitution (Prakriti)
                                    </h2>
                                    <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest">
                                        Lifelong
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {isSama(prakritiDosha) ? (
                                        <DoshaChip dosha="Sama" size="lg" />
                                    ) : (
                                        getDoshas(prakritiDosha).map(d => <DoshaChip key={d} dosha={d} size="lg" />)
                                    )}
                                </div>
                            </div>
                        </div>
                        <p className="text-emerald-700 leading-relaxed max-w-2xl relative z-10 text-sm">
                            This is your true constitutional nature. It represents your "base state" of perfect health and determines your natural strengths and traits.
                        </p>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-white/50 border-2 border-dashed border-sage-200 rounded-[2rem] p-8 text-center"
                    >
                        <p className="text-sage-500 font-bold mb-2">Complete your constitution assessment</p>
                        <p className="text-xs text-sage-400">Knowing your Prakriti helps Nani distinguish your natural traits from temporary imbalances.</p>
                    </motion.div>
                )}

                {/* 2. Vikriti Card (Current State) */}
                {showVikriti ? (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white rounded-[2rem] shadow-xl shadow-amber-900/5 p-8 border-2 relative overflow-hidden group border-amber-100"
                    >
                        <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-700">
                            <span className="text-9xl">
                                {isSama(vikritiDosha) ? '🔥' : getDoshas(vikritiDosha).map(d => DOSHA_INFO[d as keyof typeof DOSHA_INFO]?.icon).join('')}
                            </span>
                        </div>
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-6 relative z-10">
                            <div className="flex gap-1">
                                {isSama(vikritiDosha) ? (
                                    ['vata', 'pitta', 'kapha'].map(d => (
                                        <div key={d} className="text-4xl w-20 h-20 rounded-full flex items-center justify-center bg-amber-50 shadow-inner border border-amber-100">
                                            {DOSHA_INFO[d as keyof typeof DOSHA_INFO].icon}
                                        </div>
                                    ))
                                ) : (
                                    getDoshas(vikritiDosha).map(d => (
                                        <div key={d} className="text-4xl w-20 h-20 rounded-full flex items-center justify-center bg-amber-50 shadow-inner border border-amber-100">
                                            {DOSHA_INFO[d as keyof typeof DOSHA_INFO]?.icon || '🔥'}
                                        </div>
                                    ))
                                )}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h2 className="text-2xl font-serif font-bold text-amber-800">
                                        Your Current State (Vikriti)
                                    </h2>
                                    <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest">
                                        Right Now
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {isSama(vikritiDosha) ? (
                                        <DoshaChip dosha="Sama" size="lg" />
                                    ) : (
                                        getDoshas(vikritiDosha).map(d => <DoshaChip key={d} dosha={d} size="lg" />)
                                    )}
                                </div>
                            </div>
                        </div>
                        <p className="text-amber-700 leading-relaxed max-w-2xl relative z-10 text-sm">
                            This reflects your current imbalances. Nani's primary goal is to bring this state back into alignment with your natural Prakriti.
                        </p>
                    </motion.div>
                ) : userIntent === 'wellness' && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-stone-50/50 border-2 border-dashed border-stone-200 rounded-[2rem] p-8 text-center flex flex-col items-center gap-2"
                    >
                        <p className="text-stone-400 font-bold">Current health concern — not added yet</p>
                        <button 
                            onClick={() => navigate('/onboarding', { state: { mode: 'vikriti-refresh' } })}
                            className="text-xs text-stone-400 underline decoration-stone-300 hover:text-stone-500 transition-colors"
                        >
                            Complete your Vikriti assessment now
                        </button>
                    </motion.div>
                )}
            </div>

            {/* Insight Message */}
            {showPrakriti && showVikriti && (
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-center text-sage-600 font-medium italic"
                >
                    "Together, these give Nani a complete picture of your healing needs."
                </motion.p>
            )}

            {/* Connection Map (Shown only if data present) */}
            {(bodyConnections.length > 0 || lifestyleFactors.length > 0) && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white rounded-[2rem] shadow-lg shadow-sage-900/5 p-8 border border-sage-100"
                >
                    <h2 className="text-2xl font-serif font-bold text-sage-900 mb-6 flex items-center gap-3">
                        <span className="w-2 h-8 bg-sage-500 rounded-full"></span>
                        Deep Connections
                    </h2>

                    <div className="space-y-6">
                        {bodyConnections.length > 0 && (
                            <div>
                                <p className="text-xs font-bold text-sage-400 uppercase tracking-widest mb-3">Physical Signals</p>
                                {bodyConnections.map((connection, index) => (
                                    <div key={index} className="flex flex-col md:flex-row items-center gap-4 mb-3">
                                        <div className="flex-1 w-full bg-sage-50 px-6 py-4 rounded-2xl border-l-4 border-sage-500">
                                            <p className="font-bold text-sage-800">{connection.symptom}</p>
                                            <p className="text-sm text-sage-600 mt-1">{connection.location}</p>
                                        </div>
                                        <div className="text-2xl text-sage-300 rotate-90 md:rotate-0">→</div>
                                        <div className="w-full md:w-auto bg-earth-50 px-6 py-4 rounded-2xl border border-earth-100 shadow-sm min-w-[200px] text-center md:text-left">
                                            <p className="text-sm font-bold text-earth-800">{connection.category}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {lifestyleFactors.length > 0 && (
                            <div className="mt-8 pt-6 border-t border-sage-50">
                                <p className="text-xs font-bold text-sage-400 uppercase tracking-widest mb-3">Lifestyle Triggers</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {lifestyleFactors.map((factor, index) => (
                                        <div key={index} className="flex items-center gap-4 bg-white px-4 py-3 rounded-xl border border-earth-100 shadow-sm">
                                            <span className="text-2xl bg-earth-50 w-10 h-10 flex items-center justify-center rounded-full">{factor.icon}</span>
                                            <p className="text-sm font-bold text-sage-800">{factor.label}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}

            {/* CTA */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-sage-600 rounded-[2rem] p-8 md:p-12 text-white text-center shadow-xl shadow-sage-900/20"
            >
                <div className="w-16 h-16 bg-sage-500 rounded-full flex items-center justify-center text-3xl mx-auto mb-6 shadow-lg border-4 border-sage-400">🌿</div>
                <h2 className="text-2xl md:text-3xl font-serif font-bold mb-4">Your Path is Ready</h2>
                <p className="text-base mb-8 opacity-90 max-w-2xl mx-auto font-light leading-relaxed">
                    Now that Nani understands your story, we can begin your specific healing rituals and protocols.
                </p>

                <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                    <button
                        onClick={() => onComplete('chat')}
                        disabled={isSubmitting}
                        className={`bg-white text-sage-800 font-bold px-8 py-4 rounded-2xl text-lg w-full md:w-auto
                             transition-all transform ${isSubmitting
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:shadow-2xl hover:bg-earth-50 hover:scale-105'}`}
                    >
                        {isSubmitting ? 'Saving Profile...' : getCTAText()}
                    </button>
                    
                    <button
                        onClick={() => onComplete('profile')}
                        disabled={isSubmitting}
                        className={`bg-sage-500/30 text-white border-2 border-sage-400/50 font-bold px-8 py-4 rounded-2xl text-lg w-full md:w-auto
                             transition-all transform ${isSubmitting
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:bg-sage-500/50 hover:scale-105'}`}
                    >
                        Explore your Wellness Profile
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
